import { z } from "zod";

export const LEGAL_DOCUMENT_TYPES = {
  termsOfService: "terms_of_service",
  privacyPolicy: "privacy_policy",
  cookiesPolicy: "cookies_policy",
} as const;

export type TLegalDocumentType = (typeof LEGAL_DOCUMENT_TYPES)[keyof typeof LEGAL_DOCUMENT_TYPES];

export type TLegalDocument = {
  id: string;
  document_type: TLegalDocumentType;
  version: string;
  title: string;
  content: string;
  effective_at: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type TLegalDocumentSummary = Omit<TLegalDocument, "content">;

export type TLegalAcceptanceInput = {
  document_type: TLegalDocumentType;
  version: string;
};

export type TPendingLegalDocument = {
  document_type: TLegalDocumentType;
  version: string;
  title: string;
  document_id: string;
};

export type TAcceptedLegalDocument = {
  document_type: TLegalDocumentType;
  version: string;
  title: string;
  accepted_at: string;
};

export type TLegalStatus = {
  pending: TPendingLegalDocument[];
  accepted: TAcceptedLegalDocument[];
  all_required_accepted: boolean;
};

export type TLegalAcknowledgement = {
  id: string;
  user_id: string;
  document_id: string | null;
  document_type: TLegalDocumentType;
  document_version: string;
  accepted_at: string;
  ip_address: string | null;
  user_agent: string | null;
};

export type TLegalAcknowledgementList = {
  items: TLegalAcknowledgement[];
  total: number;
  page: number;
  page_size: number;
};

export type TLegalDocumentCreate = {
  document_type: TLegalDocumentType;
  version: string;
  title: string;
  content: string;
  effective_at: string;
  is_active?: boolean;
};

export type TLegalDocumentUpdate = {
  title?: string;
  content?: string;
  effective_at?: string;
  is_active?: boolean;
};

export const legalAcceptRequestSchema = z.object({
  acceptances: z
    .array(
      z.object({
        document_type: z.enum([
          LEGAL_DOCUMENT_TYPES.termsOfService,
          LEGAL_DOCUMENT_TYPES.privacyPolicy,
          LEGAL_DOCUMENT_TYPES.cookiesPolicy,
        ]),
        version: z.string().min(1),
      })
    )
    .min(1),
});

export type TLegalAcceptRequest = z.infer<typeof legalAcceptRequestSchema>;

const legalDocumentTypeSchema = z.enum([
  LEGAL_DOCUMENT_TYPES.termsOfService,
  LEGAL_DOCUMENT_TYPES.privacyPolicy,
  LEGAL_DOCUMENT_TYPES.cookiesPolicy,
]);

export const legalDocumentFormSchema = z.object({
  document_type: legalDocumentTypeSchema,
  version: z.string().trim().min(1, "Version is required"),
  title: z.string().trim().min(1, "Title is required"),
  content: z
    .string()
    .trim()
    .min(1, "Content is required")
    .refine((value) => value.replace(/<[^>]*>/g, "").trim().length > 0, "Content is required"),
  effective_at: z.string().min(1, "Effective date is required"),
  is_active: z.boolean().optional(),
});

export const legalDocumentEditFormSchema = z.object({
  title: z.string().trim().min(1, "Title is required"),
  content: z
    .string()
    .trim()
    .min(1, "Content is required")
    .refine((value) => value.replace(/<[^>]*>/g, "").trim().length > 0, "Content is required"),
  effective_at: z.string().min(1, "Effective date is required"),
});

export type TLegalDocumentFormValues = z.infer<typeof legalDocumentFormSchema>;
export type TLegalDocumentEditFormValues = z.infer<typeof legalDocumentEditFormSchema>;
